import cn from 'classnames';
import {
  type ForwardedRef,
  forwardRef,
  type MemoExoticComponent,
  memo,
  type ReactNode,
} from 'react';

import { useMemoizedBundle } from '@didaskill/ui/utils';

import { MENU_ITEM_DRAGGABLE_CLASSNAME } from './constants';
import { MenuItemButton } from './item/MenuItemButton';
import { MenuItemLink } from './item/MenuItemLink';
import { MenuContext } from './Menu.context';
import * as styles from './Menu.styles';

type MenuProps = {
  /** Show a collapsed version of the menu (items label become tooltips). */
  isCollapsed?: boolean;
  /** Menu items. */
  children?: ReactNode;
};

export const MenuBase = forwardRef(
  ({ isCollapsed = false, children = null }: MenuProps, ref: ForwardedRef<HTMLUListElement>) => {
    const contextValue = useMemoizedBundle({ isCollapsed });

    return (
      <MenuContext.Provider value={contextValue}>
        <ul ref={ref} className={cn({ isCollapsed })} css={styles.container}>
          {children}
        </ul>
      </MenuContext.Provider>
    );
  },
);

MenuBase.displayName = 'Menu';

export const Menu: MemoExoticComponent<typeof MenuBase> & {
  ItemLink?: typeof MenuItemLink;
  ItemButton?: typeof MenuItemButton;
  ITEM_DRAGGABLE_CLASSNAME?: typeof MENU_ITEM_DRAGGABLE_CLASSNAME;
} = memo(MenuBase);

Menu.ItemLink = MenuItemLink;
Menu.ItemButton = MenuItemButton;
Menu.ITEM_DRAGGABLE_CLASSNAME = MENU_ITEM_DRAGGABLE_CLASSNAME;
